import React, { useState, useEffect } from 'react';
import { 
  FileText, Loader2, Search, Download, Check, X, Eye, Trash2,
  Filter, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { adminCertificatesAPI, groupsAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CertificatesPage = () => {
  const [certificates, setCertificates] = useState([]);
  const [stats, setStats] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [levels, setLevels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterVerified, setFilterVerified] = useState('');

  useEffect(() => {
    fetchCertificates();
  }, [filterGroup, filterLanguage, filterLevel, filterVerified]);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterGroup) params.group = filterGroup;
      if (filterLanguage) params.language = filterLanguage;
      if (filterLevel) params.level = filterLevel;
      if (filterVerified) params.is_verified = filterVerified;
      if (searchTerm) params.search = searchTerm;
      
      const response = await adminCertificatesAPI.getAll(params);
      setCertificates(response.data.certificates || []);
      setStats(response.data.stats || null);
      setLanguages(response.data.languages || []);
      setLevels(response.data.levels || []);
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCertificates();
  };

  const verifyCertificate = async (id, isVerified) => {
    try {
      await adminCertificatesAPI.verify(id, isVerified);
      fetchCertificates();
    } catch (error) {
      alert('Xatolik yuz berdi');
    }
  };

  const deleteCertificate = async (id) => {
    if (window.confirm("Sertifikatni o'chirmoqchimisiz?")) {
      try {
        await adminCertificatesAPI.delete(id);
        fetchCertificates();
      } catch (error) {
        alert('Xatolik yuz berdi');
      }
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const exportToExcel = () => {
    const data = certificates.map(cert => ({
      'Talaba': cert.student_name,
      'ID': cert.student_id,
      'Guruh': cert.group_name || '-',
      'Til': cert.language_display,
      'Daraja': cert.level,
      'Sertifikat nomi': cert.certificate_name || '-',
      'Ball': cert.score || '-',
      'Berilgan sana': cert.issue_date,
      'Amal qilish': cert.expiry_date || '-',
      'Holat': cert.is_verified ? 'Tasdiqlangan' : 'Tekshiruvda',
      'Yuklangan': new Date(cert.created_at).toLocaleDateString('uz-UZ'),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sertifikatlar');
    XLSX.writeFile(wb, `til_sertifikatlari_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const filteredCertificates = certificates.filter(cert =>
    cert.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.certificate_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><FileText size={28} /> Til sertifikatlari</h1>
          <p>Talabalar yuklagan til sertifikatlari</p>
        </div>
        <button className="btn btn-secondary" onClick={exportToExcel}>
          <Download size={18} />
          Excel
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total"><FileText size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Jami sertifikatlar</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success"><CheckCircle size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.verified}</span>
              <span className="stat-label">Tasdiqlangan</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning"><Clock size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.pending}</span>
              <span className="stat-label">Kutilmoqda</span>
            </div>
          </div>
        </div>
      )}

      {/* Til bo'yicha statistika */}
      {stats?.by_language && Object.keys(stats.by_language).length > 0 && (
        <div className="language-stats-row">
          {Object.entries(stats.by_language).map(([key, data]) => (
            <div key={key} className="lang-stat-item">
              <span className="lang-name">{data.name}</span>
              <span className="lang-count">{data.count}</span>
              <span className="lang-verified">({data.verified} ✓)</span>
            </div>
          ))}
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <form onSubmit={handleSearch} className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Talaba qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
        <select 
          className="filter-select"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
        >
          <option value="">Barcha guruhlar</option>
          {groups.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterLanguage}
          onChange={(e) => setFilterLanguage(e.target.value)}
        >
          <option value="">Barcha tillar</option>
          {languages.map(l => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
        >
          <option value="">Barcha darajalar</option>
          {levels.map(l => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterVerified}
          onChange={(e) => setFilterVerified(e.target.value)}
        >
          <option value="">Barchasi</option>
          <option value="true">Tasdiqlangan</option>
          <option value="false">Kutilmoqda</option>
        </select>
      </div>

      {/* Jadval */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : filteredCertificates.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>Sertifikatlar topilmadi</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Talaba</th>
                <th>Guruh</th>
                <th>Til</th>
                <th>Daraja</th>
                <th>Sertifikat</th>
                <th>Sana</th>
                <th>Holat</th>
                <th>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {filteredCertificates.map(cert => (
                <tr key={cert.id}>
                  <td>
                    <div className="user-cell">
                      <span className="user-name">{cert.student_name}</span>
                      <span className="user-id">{cert.student_id}</span>
                    </div>
                  </td>
                  <td>{cert.group_name || '-'}</td>
                  <td>{cert.language_display}</td>
                  <td>
                    <span className="level-badge">{cert.level}</span>
                  </td>
                  <td>
                    <div className="cert-info">
                      {cert.certificate_name && <span className="cert-name">{cert.certificate_name}</span>}
                      {cert.score && <span className="cert-score">Ball: {cert.score}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="date-cell">
                      <span>{new Date(cert.issue_date).toLocaleDateString('uz-UZ')}</span>
                      {cert.expiry_date && (
                        <span className="expiry">→ {new Date(cert.expiry_date).toLocaleDateString('uz-UZ')}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${cert.is_verified ? 'success' : 'warning'}`}>
                      {cert.is_verified ? (
                        <><Check size={14} /> Tasdiqlangan</>
                      ) : (
                        <><Clock size={14} /> Kutilmoqda</>
                      )}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {cert.certificate_file && (
                        <a 
                          href={getFileUrl(cert.certificate_file)} 
                          download
                          className="btn btn-icon btn-info"
                          title="Yuklab olish"
                        >
                          <Download size={18} />
                        </a>
                      )}
                      <a 
                        href={getFileUrl(cert.certificate_file)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-icon"
                        style={{ background: '#f1f5f9', color: '#475569' }}
                        title="Faylni ko'rish"
                      >
                        <Eye size={18} />
                      </a>
                      {!cert.is_verified ? (
                        <button 
                          className="btn btn-icon btn-success"
                          onClick={() => verifyCertificate(cert.id, true)}
                          title="Tasdiqlash"
                        >
                          <Check size={18} />
                        </button>
                      ) : (
                        <button 
                          className="btn btn-icon btn-warning"
                          onClick={() => verifyCertificate(cert.id, false)}
                          title="Tasdiqdan qaytarish"
                        >
                          <X size={18} />
                        </button>
                      )}
                      <button 
                        className="btn btn-icon btn-danger"
                        onClick={() => deleteCertificate(cert.id)}
                        title="O'chirish"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CertificatesPage;
